import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Text,
  Button,
  Avatar,
  VStack,
  HStack,
  useToast,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Heading,
  Flex,
  Divider,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/FrontendPathConstants';
import Chatbot from '../../components/ChatBot/Chatbot';
import { useChangePassword, useChangeProfilePhoto } from '../../api/projectApi';

interface UserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  photoUrl?: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [userData, setUserData] = useState<UserData | null>(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    old?: string;
    new?: string;
    confirm?: string;
  }>({});
  const {
    changePassword,
    isLoading: isChangingPassword,
  } = useChangePassword();
  const {
    changeProfilePhoto,
  } = useChangeProfilePhoto();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const stored = localStorage.getItem('userData');
    if (stored) setUserData(JSON.parse(stored));
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userData');
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    navigate(ROUTES.LOGIN);
  };

  const openChangeModal = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    onOpen();
  };

  const handleSubmitChange = async () => {
    const errs: typeof errors = {};
    if (!oldPassword) errs.old = 'Previous password is required';
    if (!newPassword) errs.new = 'New password is required';
    if (newPassword !== confirmPassword)
      errs.confirm = 'Passwords do not match';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    const result = await changePassword(oldPassword, newPassword);
    setLoading(false);

    if (!result.success) {
      toast({
        title: 'Error',
        description: result.error,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Success',
        description: 'Password updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    }
  };

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const toastId = toast({
      title: 'Uploading photo...',
      status: 'info',
      duration: 1000,
      isClosable: true,
    });

    try {
      const result = await changeProfilePhoto(formData);
      toast.update(toastId, {
        title: 'Profile photo updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      const updatedUserData = {
        ...userData,
        photoUrl: `${import.meta.env.VITE_API_URL}${result.data!.url}?t=${Date.now()}`,
      };
      setUserData(updatedUserData);
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
    } catch (e) {
      toast.update(toastId, {
        title: 'Error',
        description: 'Photo upload failed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="surface.100" py={8} px={4}>
      <Box maxW="800px" mx="auto">
        {/* Header */}
        <Heading
          size="lg"
          color="primary.500"
          mb={8}
          textAlign="center"
        >
          Profile Settings
        </Heading>

        {userData ? (
          <Card
            bg="white"
            borderRadius="16px"
            boxShadow="0 4px 20px rgba(26, 18, 98, 0.08)"
            border="1px solid"
            borderColor="surface.300"
          >
            <CardBody p={{ base: 6, md: 10 }}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={10}
                align={{ base: 'center', md: 'start' }}
              >
                {/* Avatar Section */}
                <VStack spacing={4}>
                  <Avatar
                    size="2xl"
                    name={`${userData.first_name || ''} ${userData.last_name || ''}`}
                    src={userData.photoUrl}
                    bg="primary.500"
                    color="white"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change Photo
                  </Button>
                </VStack>

                {/* Info Section */}
                <VStack spacing={5} align="stretch" flex="1" w="100%">
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      color="muted.400"
                      mb={1}
                    >
                      First Name
                    </Text>
                    <Text fontSize="lg" color="primary.500" fontWeight="medium">
                      {userData.first_name || 'Not set'}
                    </Text>
                  </Box>

                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      color="muted.400"
                      mb={1}
                    >
                      Last Name
                    </Text>
                    <Text fontSize="lg" color="primary.500" fontWeight="medium">
                      {userData.last_name || 'Not set'}
                    </Text>
                  </Box>

                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      color="muted.400"
                      mb={1}
                    >
                      Email Address
                    </Text>
                    <Text fontSize="lg" color="primary.500" fontWeight="medium">
                      {userData.email || 'Not set'}
                    </Text>
                  </Box>

                  <Divider borderColor="surface.300" my={2} />

                  {/* Actions */}
                  <HStack spacing={4} pt={2}>
                    <Button
                      variant="primary"
                      onClick={openChangeModal}
                    >
                      Change Password
                    </Button>

                    <Button
                      variant="ghost"
                      color="error.500"
                      _hover={{ bg: 'surface.200' }}
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </HStack>
                </VStack>
              </Flex>
            </CardBody>
          </Card>
        ) : (
          <Card bg="white" borderRadius="16px" p={10}>
            <Text textAlign="center" color="muted.400">
              No user data found.
            </Text>
          </Card>
        )}
      </Box>

      {/* Change Password Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="rgba(26, 18, 98, 0.4)" />
        <ModalContent borderRadius="16px" mx={4}>
          <ModalHeader color="primary.500" fontWeight="bold">
            Change Password
          </ModalHeader>
          <ModalCloseButton color="muted.400" />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.old}>
                <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                  Current Password
                </FormLabel>
                <Input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoFocus
                />
                <FormErrorMessage>{errors.old}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.new}>
                <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                  New Password
                </FormLabel>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <FormErrorMessage>{errors.new}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirm}>
                <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                  Confirm New Password
                </FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <FormErrorMessage>{errors.confirm}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={onClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitChange}
              isLoading={loading}
            >
              Update Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box flexShrink={0}>
        <Chatbot />
      </Box>
    </Box>
  );
};

export default ProfilePage;
